// programs.jsx — Programs tab (your programs + the program library), the template
// detail sheet, and the ProgramEditor. Routes: 'programs', 'program-edit' (params.programId).
//
// Shared with auth.jsx (onboarding): TemplateCard, TemplateDetailSheet, SplitStrip,
// useStartTemplate, PIcon, GoalTile.

import React from 'react';
import { ConfirmDialog } from './confirm_dialog.jsx';
import { LLSheet, VideoPlayerSheet } from './sheets.jsx';
import { parseYouTube, prescriptionFor } from './utils.jsx';
import { fmtWeight, unitLabel, useApp } from './store.jsx';
import { LL, LLIcon } from './tokens.jsx';
import { PageHeader } from './phone.jsx';
import { ExercisePicker, ExerciseThumb, FilterChip, SearchField } from './exercise_picker.jsx';
import { catalogEntryFor, loadCatalog, useCatalog } from './exercise_catalog.js';
import { VOLUME_LANDMARKS } from './analytics.js';
import { SchemeEditor, CycleCard, cyclePatch } from './program_authoring.jsx';
import { ShareProgramSheet, ImportLinkSheet, SharedProgramSheet, useSharedLinkToken } from './program_share_ui.jsx';
import {
  TEMPLATES, GOALS, LEVEL_LABEL, GOAL_LABEL, EQUIPMENT_TAGS,
  buildProgramFromTemplate, templateStats, templateVolume,
  resolveTemplateExercise, profileEquipment,
} from './program_templates.js';
import { matchPrograms, scoreMatch } from './program_match.js';
import { useSimple, Explain } from './explain.jsx';
import { plainEffortShort } from './glossary.js';
import {
  WAVES, SCHEMES, schemeOf, prescribe, rowsSummary, cycleOf, programWeekInfo, tmLifts, missingTMs,
  trainingMaxFrom, withTrainingMaxes, withCurrentWeek, restartPlan, schemeLabel, PRESETS, applyPreset, nextWorkout,
} from './progression.js';

import {
  WEEK, kicker, card, meta, GOAL_HUE, fmtRest, fmtReps, reducedMotion, ProgramStyles, PIcon, templateIcon,
  GoalTile, SplitStrip, Pill, Btn, IconBtn, Sheet, ActionSheet, StatTile, SCHEME_SHORT, ScrollTopOnMount,
  weekName, SchemeTag, WeekStrip, toDisp, fromDisp, Row, NumField, Field,
} from './program_ui.jsx';

// Shared with auth.jsx (onboarding).
export { ProgramStyles, PIcon, GoalTile, SplitStrip };

// ——— Helpers ———————————————————————————————————————————————————————————————

export function splitLabel(p) {
  return String(p?.split || '').replace(/^\s*\d+\s*days?\s*[·•|-]\s*/i, '').trim();
}
function programMeta(p) {
  const n = p.days?.length ?? 0;
  const wk = p.weeks ?? p.periodization?.weeks?.length ?? null;
  const parts = [`${n} day${n === 1 ? '' : 's'}/wk`];
  const sl = splitLabel(p);
  if (sl) parts.push(sl);
  if (wk) parts.push(`${wk} wk`);
  return parts.join(' · ');
}
function estDayMinutes(program, day) {
  const secs = (day.exercises || []).reduce((a, pe) => {
    const rx = prescriptionFor(program, pe);
    const sets = rx.sets ?? pe.sets ?? 3;
    return a + sets * (45 + (rx.rest ?? pe.rest ?? 120));
  }, 0);
  return Math.max(5, Math.round(secs / 60 / 5) * 5);
}

// ——— Starting a template ——————————————————————————————————————————————————

// Returns async start(template, { profile, units }) → programId. Copies any
// catalog exercises into the library first so every program exercise has a real id.
export function useStartTemplate() {
  const { state, actions } = useApp();
  const ref = React.useRef(state);
  ref.current = state;
  return React.useCallback(async (template, opts = {}) => {
    let catalog = null;
    try { catalog = await loadCatalog(); } catch { catalog = null; }
    const s = ref.current;
    const profile = opts.profile || s.profile || null;
    const units = opts.units || s.prefs?.units || 'lb';
    const built = buildProgramFromTemplate(template, { exercises: s.exercises, catalog, profile, units, state: s });
    const { newExercises } = built;
    const tms = opts.tms || takePendingTMs(template.id);
    const program = tms ? withTrainingMaxes(built.program, tms) : built.program;
    if (newExercises.length) actions.ensureExercises(newExercises);
    const id = actions.addProgram(program);
    actions.markWelcomed();
    return id;
  }, [actions]);
}

// ——— Template card + detail ——————————————————————————————————————————————

export function TemplateCard({ template: t, onOpen, badge, reasons, warnings, compact = false, index = 0, featured = false }) {
  const Icon = templateIcon(t);
  const st = templateStats(t);
  const anim = reducedMotion() ? undefined : `llpFadeUp ${LL.motion.slow}ms ${LL.ease} both ${Math.min(index, 8) * 40}ms`;
  return (
    <button type="button" className="llp-anim" onClick={() => onOpen && onOpen(t)} style={{
      ...card, textAlign: 'left', padding: featured ? 18 : 14, cursor: 'pointer', width: '100%',
      display: 'flex', flexDirection: 'column', gap: 10, color: LL.fg,
      borderColor: featured ? `color-mix(in oklab, ${LL.accent} 55%, ${LL.line})` : LL.line,
      background: featured ? `linear-gradient(160deg, color-mix(in oklab, ${LL.accent} 9%, ${LL.surface}), ${LL.surface} 60%)` : LL.surface,
      animation: anim,
    }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', width: '100%' }}>
        <GoalTile goal={t.goal} icon={Icon} size={featured ? 48 : 44} />
        <div style={{ flex: 1, minWidth: 0 }}>
          {badge && <div style={{ marginBottom: 6 }}><Pill tone="accent"><PIcon.star style={{ width: 13, height: 13 }} />{badge}</Pill></div>}
          <div style={{ fontFamily: LL.display, fontSize: featured ? LL.fs.stat : LL.fs.lg, fontWeight: 700, letterSpacing: -0.3, lineHeight: 1.15, color: LL.fg }}>{t.name}</div>
          <div style={{ ...meta, marginTop: 3 }}>
            {t.daysPerWeek} days · {t.minutes} min · {LEVEL_LABEL[t.level]}
          </div>
        </div>
        {!compact && <LLIcon.chev style={{ color: LL.fgFaint, flexShrink: 0, marginTop: 2 }} />}
      </div>
      {!compact && <div style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgMute, lineHeight: 1.45 }}>{t.tagline}</div>}
      {reasons && reasons.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {reasons.map(r => (
            <div key={r} style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fg }}>
              <ReasonTick />
              {r}
            </div>
          ))}
        </div>
      )}
      {warnings && warnings.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, ...meta, color: `color-mix(in oklab, ${LL.warn} 70%, ${LL.fg})` }}>
          <PIcon.warn /> {warnings[0]}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, width: '100%' }}>
        <SplitStrip days={t.days.map(d => d.day)} />
        <span style={{ ...meta, fontSize: LL.fs.sm, color: LL.fgDim }}>{st.exCount} exercises · {t.weeks} wk</span>
      </div>
    </button>
  );
}

// The "why it fits" tick: a small accent-soft circle with a check (not a
// selection control, so not LLCheck).
function ReasonTick() {
  return (
    <span aria-hidden="true" style={{ width: 18, height: 18, borderRadius: LL.r.pill, background: LL.accentSoft, color: LL.accentText, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <PIcon.check style={{ width: 12, height: 12 }} />
    </span>
  );
}

function VolumeBars({ rows }) {
  const max = Math.max(20, ...rows.map(r => r.sets));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      {rows.map(r => {
        const lm = VOLUME_LANDMARKS[r.muscle];
        const inRange = lm ? r.sets >= lm.mev : true;
        return (
          <div key={r.muscle} style={{ display: 'grid', gridTemplateColumns: '92px 1fr 40px', alignItems: 'center', gap: 10 }}>
            <span style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgMute, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.muscle}</span>
            <div style={{ height: 8, borderRadius: LL.r.xs, background: LL.surface2, position: 'relative', overflow: 'hidden' }}>
              <div className="llp-anim" style={{
                position: 'absolute', left: 0, top: 0, bottom: 0, width: `${(r.sets / max) * 100}%`, borderRadius: LL.r.xs,
                background: inRange ? LL.accent : `color-mix(in oklab, ${LL.accent} 40%, ${LL.surface3})`,
                transformOrigin: 'left', animation: reducedMotion() ? undefined : `llpGrow .7s ${LL.ease} both`,
              }} />
              {lm && lm.mev > 0 && <div title="Minimum effective volume" style={{ position: 'absolute', top: 0, bottom: 0, left: `${(lm.mev / max) * 100}%`, width: 2, background: LL.bg, opacity: 0.8 }} />}
            </div>
            <span style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fg, fontWeight: 600, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.sets}</span>
          </div>
        );
      })}
      <style>{'@keyframes llpGrow { from { transform: scaleX(0); } to { transform: scaleX(1); } }'}</style>
    </div>
  );
}

function thumbItem(ex, catalog) {
  if (!ex) return null;
  const entry = ex.img ? ex : catalogEntryFor(ex.id, catalog);
  return { img: entry?.img || null, primary: ex.primary || entry?.primary || (ex.group ? [ex.group] : []), secondary: ex.secondary || entry?.secondary || [] };
}

export function TemplateDetailSheet({ template: t, open, onClose, onStart, starting, profile }) {
  const { state } = useApp();
  const { simple } = useSimple();
  const TMW = simple ? 'working max' : 'training max'; // simple mode's plain name
  const catalog = useCatalog();
  const units = state.prefs?.units || 'lb';
  // Training-max step: percent programs ask for a recent best set per main lift.
  const [step, setStep] = React.useState('detail');
  const [tmValues, setTmValues] = React.useState({});
  React.useEffect(() => { setStep('detail'); setTmValues({}); }, [t?.id, open]);
  const tmProgram = React.useMemo(() => {
    if (!t || !t.days.some(d => d.exercises.some(e => e.scheme === 'percent'))) return null;
    return buildProgramFromTemplate(t, { exercises: state.exercises, catalog, units, state }).program;
  }, [t, catalog, state, units]);
  const tmList = React.useMemo(() => (tmProgram ? tmLifts(tmProgram) : []), [tmProgram]);
  const tmReady = tmsFromForm(tmList, tmValues, units);
  const startNow = (withTms) => {
    if (withTms && t) _pendingTMs.set(t.id, withTms);
    onStart && onStart(t);
  };
  const vol = React.useMemo(() => (t ? templateVolume(t, state.exercises, catalog).filter(r => r.sets >= 2).slice(0, 10) : []), [t, catalog, state.exercises]);
  const fit = React.useMemo(() => (t && profile ? scoreMatch(t, profile) : null), [t, profile]);
  if (!t) return null;
  const st = templateStats(t);
  const missingEquip = profile ? (() => { const have = profileEquipment(profile); return have ? t.equipment.filter(e => !have.includes(e)) : []; })() : [];
  const equipLabels = t.equipment.length ? t.equipment.map(e => EQUIPMENT_TAGS.find(x => x.id === e)?.label || e) : ['No equipment'];
  const schemes = [...new Set(t.days.flatMap(d => d.exercises.map(e => e.scheme || 'double')))].filter(x => x !== 'double');
  if (step === 'tms') {
    const nReady = Object.keys(tmReady).length;
    return (
      <Sheet open={open} onClose={onClose} title={`Set your ${TMW}es`} subtitle={t.name}
        footer={
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Btn full size="lg" onClick={() => startNow(tmReady)} disabled={starting || !nReady}>{starting ? 'Setting up…' : nReady < tmList.length ? `Start with ${nReady} of ${tmList.length}` : 'Start program'}</Btn>
            <Btn full tone="ghost" size="sm" onClick={() => startNow(Object.keys(tmReady).length ? tmReady : null)} disabled={starting}>Skip — I'll set them before my first workout</Btn>
          </div>
        }>
        <ScrollTopOnMount />
        <div style={{ fontFamily: LL.ui, fontSize: LL.fs.body, color: LL.fgMute, lineHeight: 1.5, marginBottom: 14 }}>
          {simple
            ? 'Every weight in this program is a percentage of a working max — a number a little under the most you could lift once. Enter a recent good set for each lift and we’ll work it out.'
            : 'Every load in this program is a percentage of a training max. Enter a recent best set for each lift and we’ll set it to 90% of your estimated max.'}
        </div>
        <TrainingMaxForm lifts={tmList} units={units} values={tmValues} onChange={(k, v) => setTmValues(s => ({ ...s, [k]: v }))} />
        <div style={{ height: 8 }} />
        <Btn tone="ghost" size="sm" onClick={() => setStep('detail')} icon={<LLIcon.back style={{ width: 16, height: 16 }} />}>Back to the program</Btn>
      </Sheet>
    );
  }
  return (
    <Sheet open={open} onClose={onClose} title={t.name} subtitle={`${GOAL_LABEL[t.goal]} · ${LEVEL_LABEL[t.level]}`}
      footer={<Btn full size="lg" onClick={() => (tmList.length ? setStep('tms') : startNow(null))} disabled={starting}>{starting ? 'Setting up…' : tmList.length ? `Start: set ${TMW}es` : 'Start this program'}</Btn>}>
      <div style={{ fontFamily: LL.ui, fontSize: LL.fs.body, color: LL.fgMute, lineHeight: 1.5 }}>{t.description}</div>

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <StatTile label="Days / wk" value={t.daysPerWeek} />
        <StatTile label="Per session" value={t.minutes} unit="min" />
        <StatTile label="Sets / wk" value={st.setsPerWeek} />
      </div>

      <div style={{ marginTop: 14, ...card, padding: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={kicker}>Weekly schedule</div>
          <span style={{ ...meta, fontSize: LL.fs.sm }}>{t.weeks}-week block</span>
        </div>
        <SplitStrip days={t.days.map(d => d.day)} size="lg" />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
          {equipLabels.map(l => <Pill key={l}>{l}</Pill>)}
        </div>
        {missingEquip.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, ...meta, color: `color-mix(in oklab, ${LL.warn} 70%, ${LL.fg})` }}>
            <PIcon.warn /> You told us you don't have: {missingEquip.map(e => EQUIPMENT_TAGS.find(x => x.id === e)?.label.toLowerCase() || e).join(', ')}
          </div>
        )}
      </div>

      {fit && fit.reasons.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={{ ...kicker, margin: '0 2px 8px' }}>Why it fits you</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {fit.reasons.map(r => (
              <div key={r} style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: LL.ui, fontSize: LL.fs.body, color: LL.fg }}>
                <ReasonTick />
                {r}
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ marginTop: 18 }}>
        <div style={{ ...kicker, margin: '0 2px 8px' }}>How you progress</div>
        {schemes.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
            {schemes.map(sc => <Pill key={sc} tone="accent">{SCHEMES[sc]?.label || sc}</Pill>)}
            <Pill>Automatic</Pill>
          </div>
        )}
        <div style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgMute, lineHeight: 1.5 }}>{t.progression}</div>
      </div>
      <TemplateWeeks t={t} />

      {vol.length > 0 && (
        <div style={{ marginTop: 18, ...card, padding: 14 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={kicker}>Hard sets per week</div>
            <span style={{ ...meta, fontSize: LL.fs.sm, color: LL.fgDim }}>tick = minimum for growth</span>
          </div>
          <VolumeBars rows={vol} />
        </div>
      )}

      <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {t.days.map((d, di) => (
          <div key={di} style={{ ...card, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderBottom: `1px solid ${LL.line}` }}>
              <div style={{ width: 42, height: 42, borderRadius: LL.r.md, background: LL.accentSoft, color: LL.accentText, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <span style={{ fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase' }}>{d.day}</span>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: LL.display, fontSize: LL.fs.lg, fontWeight: 700, color: LL.fg }}>{d.name}</div>
                <div style={{ ...meta, fontSize: LL.fs.sm, marginTop: 1 }}>{d.focus}</div>
              </div>
            </div>
            <div style={{ padding: '4px 0' }}>
              {d.exercises.map((e, ei) => {
                const ex = resolveTemplateExercise(e.x, state.exercises, catalog);
                const prev = d.exercises[ei - 1], next = d.exercises[ei + 1];
                const ssStart = e.ss && prev?.ss !== e.ss;
                const inSS = !!e.ss;
                const ssEnd = e.ss && next?.ss !== e.ss;
                const isSec = ex?.unit === 'sec';
                return (
                  <div key={ei} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 14px', position: 'relative' }}>
                    {inSS && <div style={{ position: 'absolute', left: 5, top: ssStart ? '50%' : 0, bottom: ssEnd ? '50%' : 0, width: 3, borderRadius: LL.r.xs, background: LL.accent, opacity: 0.8 }} />}
                    <ExerciseThumb item={thumbItem(ex, catalog)} size={40} radius={LL.r.sm} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, color: LL.fg, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ex?.name || e.x}</div>
                      <div style={{ ...meta, fontSize: LL.fs.sm, marginTop: 1 }}>
                        {templateExerciseLine(e, t, simple) || `${e.sets} × ${fmtReps(e.repMin, e.repMax)}${isSec ? ' s' : ''}`} · rest {fmtRest(e.rest)}{ssStart ? ' · superset' : ''}
                      </div>
                      {e.note && <div style={{ ...meta, fontSize: LL.fs.sm, color: LL.fgDim, marginTop: 2 }}>{e.note}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <div style={{ ...meta, fontSize: LL.fs.sm, color: LL.fgDim, textAlign: 'center', marginTop: 16 }}>
        {tmList.length ? `Loads come from your ${TMW}es` : 'Starting weights are set from your history'}{profile?.experience && !tmList.length ? ' and experience' : ''} — everything is editable after you start. Weights in {unitLabel(units)}.
      </div>
    </Sheet>
  );
}

// ——— Library section ————————————————————————————————————————————————————

// Facets: goal, days a week, equipment, level, session length.
const GOAL_ORDER = ['hypertrophy', 'strength', 'powerbuilding', 'general'];
const GOAL_SECTION = { hypertrophy: 'Build muscle', strength: 'Get stronger', powerbuilding: 'Strength and size', general: 'Fitness and health' };
const FACETS = [
  { id: 'goal', label: 'Goal', options: GOAL_ORDER.map(g => ({ id: g, label: GOAL_SECTION[g] })) },
  { id: 'days', label: 'Days', options: [2, 3, 4, 5, 6].map(d => ({ id: d, label: `${d} days` })) },
  { id: 'equip', label: 'Equipment', options: [
    { id: 'gym', label: 'Full gym' }, { id: 'dumbbells', label: 'Dumbbells' }, { id: 'kettlebell', label: 'Kettlebell' },
    { id: 'bodyweight', label: 'Bodyweight' },
  ] },
  { id: 'level', label: 'Level', options: [{ id: 'beginner', label: 'Beginner' }, { id: 'intermediate', label: 'Intermediate' }, { id: 'advanced', label: 'Advanced' }] },
  { id: 'len', label: 'Length', options: [{ id: 30, label: '≤ 30 min' }, { id: 45, label: '≤ 45 min' }, { id: 60, label: '≤ 60 min' }, { id: 99, label: '60+ min' }] },
];
function matchesEquip(t, f) {
  if (!f) return true;
  const eq = t.equipment;
  if (f === 'bodyweight') return eq.every(e => e === 'pullup' || e === 'bands');
  if (f === 'kettlebell') return eq.includes('kettlebell');
  if (f === 'dumbbells') return eq.length > 0 && eq.every(e => e === 'dumbbell' || e === 'bench');
  return eq.some(e => e === 'barbell' || e === 'machine' || e === 'cable');
}
const matchesLen = (t, v) => !v || (v === 99 ? t.minutes > 60 : t.minutes <= v);
const matchesLevel = (t, v) => !v || t.levels.includes(v) || (v === 'beginner' && t.levels.includes('new'));
function matchesQuery(t, q) {
  if (!q) return true;
  const hay = [t.name, t.tagline, t.split, t.description, GOAL_SECTION[t.goal], LEVEL_LABEL[t.level],
    ...t.equipment.map(e => EQUIPMENT_TAGS.find(x => x.id === e)?.label || e), t.equipment.length ? '' : 'bodyweight no equipment home']
    .join(' ').toLowerCase();
  return q.toLowerCase().split(/\s+/).filter(Boolean).every(w => hay.includes(w.replace(/s$/, '')));
}

// Seven 6px squares, training days filled.
function WeekDots({ days }) {
  const set = new Set(days);
  return (
    <span aria-label={`Trains ${days.join(', ')}`} style={{ display: 'inline-flex', gap: 3, flexShrink: 0 }}>
      {WEEK.map(d => <span key={d} style={{ width: 6, height: 6, borderRadius: LL.r.xs, background: set.has(d) ? LL.accent : LL.surface3 }} />)}
    </span>
  );
}

// 84px library row: goal tile, name, "3 days · 50 min · Beginner" and the week strip.
export function TemplateRow({ template: t, onOpen, badge, last }) {
  const Icon = templateIcon(t);
  return (
    <button type="button" onClick={() => onOpen && onOpen(t)} className="llp-row" style={{
      width: '100%', minHeight: 84, display: 'flex', alignItems: 'center', gap: 12, padding: '12px 4px', boxSizing: 'border-box',
      background: 'transparent', border: 'none', borderBottom: last ? 'none' : `1px solid ${LL.line}`, cursor: 'pointer', textAlign: 'left', color: LL.fg,
    }}>
      <GoalTile goal={t.goal} icon={Icon} size={40} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: LL.ui, fontSize: LL.fs.lg, fontWeight: 600, letterSpacing: -0.2, lineHeight: 1.22, color: LL.fg, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', overflowWrap: 'anywhere' }}>{t.name}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 5 }}>
          <span style={{ ...meta, flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {badge && <span style={{ color: LL.accentText, fontWeight: 600 }}>{badge} · </span>}
            {t.daysPerWeek} days · {t.minutes} min · {LEVEL_LABEL[t.level]}
          </span>
          <WeekDots days={t.days.map(d => d.day)} />
        </div>
      </div>
    </button>
  );
}

function ProgramLibrary({ onOpen, profile }) {
  const [q, setQ] = React.useState('');
  const [f, setF] = React.useState({ goal: null, days: null, equip: null, level: null, len: null });
  const [facet, setFacet] = React.useState(null);
  const ranked = React.useMemo(() => matchPrograms(profile || {}), [profile]);
  const hasProfile = !!(profile && (profile.goal || profile.daysPerWeek || profile.experience));
  // Without a profile: beginner → advanced, then fewer days first.
  const LV = { new: 0, beginner: 1, intermediate: 2, advanced: 3 };
  const order = hasProfile ? ranked : TEMPLATES.map(t => ({ template: t, fit: 0, equipOk: true }))
    .sort((a, b) => (LV[a.template.level] - LV[b.template.level]) || (a.template.daysPerWeek - b.template.daysPerWeek) || (a.template.minutes - b.template.minutes));
  const filtering = Object.values(f).some(v => v != null) || !!q.trim();
  const list = order.filter(({ template: t }) =>
    (!f.goal || t.goal === f.goal) && (!f.days || t.daysPerWeek === f.days) && matchesEquip(t, f.equip)
    && matchesLevel(t, f.level) && matchesLen(t, f.len) && matchesQuery(t, q.trim()));
  const picks = hasProfile && !filtering ? ranked.filter(r => r.equipOk).slice(0, 3) : [];
  const groups = GOAL_ORDER.map(g => ({ g, rows: list.filter(r => r.template.goal === g) })).filter(x => x.rows.length);
  const clear = () => { setF({ goal: null, days: null, equip: null, level: null, len: null }); setQ(''); setFacet(null); };
  const valueLabel = (fc) => fc.options.find(o => o.id === f[fc.id])?.label;
  const open = FACETS.find(fc => fc.id === facet);
  const header = (title, count) => (
    <div style={{ position: 'sticky', top: 0, zIndex: 2, background: LL.bg, padding: '16px 4px 6px', display: 'flex', alignItems: 'baseline', gap: 8, borderBottom: `1px solid ${LL.line}` }}>
      <span style={kicker}>{title}</span>
      <span style={{ ...kicker, color: LL.fgFaint, fontVariantNumeric: 'tabular-nums' }}>{count}</span>
    </div>
  );
  return (
    <div>
      <SearchField value={q} onChange={setQ} placeholder={`Search ${TEMPLATES.length} programs`} />
      <div className="llp-noscroll" role="toolbar" aria-label="Filter programs" style={{ display: 'flex', gap: 8, overflowX: 'auto', margin: '10px -16px 0', padding: '0 16px 2px', scrollbarWidth: 'none' }}>
        {FACETS.map(fc => {
          const v = valueLabel(fc);
          return (
            <FilterChip key={fc.id} active={facet === fc.id || !!v} onClick={() => setFacet(facet === fc.id ? null : fc.id)} ariaLabel={`${fc.label}${v ? `: ${v}` : ''}`}>
              {v || fc.label}
              <LLIcon.chevDown style={{ width: 14, height: 14, marginRight: -4, transform: facet === fc.id ? 'rotate(180deg)' : 'none', transition: `transform ${LL.motion.base}ms ${LL.ease}` }} />
            </FilterChip>
          );
        })}
        {filtering && <FilterChip onClick={clear}>Clear</FilterChip>}
      </div>
      <div style={{ display: 'grid', gridTemplateRows: open ? '1fr' : '0fr', transition: `grid-template-rows ${reducedMotion() ? 0 : 220}ms ${LL.ease}` }}>
        <div style={{ overflow: 'hidden' }}>
          {open && (
            <div role="radiogroup" aria-label={open.label} style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '10px 0 2px' }}>
              <FilterChip active={f[open.id] == null} onClick={() => { setF(s => ({ ...s, [open.id]: null })); setFacet(null); }}>Any {open.label.toLowerCase()}</FilterChip>
              {open.options.map(o => (
                <FilterChip key={String(o.id)} active={f[open.id] === o.id} onClick={() => { setF(s => ({ ...s, [open.id]: s[open.id] === o.id ? null : o.id })); setFacet(null); }}>{o.label}</FilterChip>
              ))}
            </div>
          )}
        </div>
      </div>
      {picks.length > 0 && (
        <section aria-label="Top picks for you">
          {header('Top picks for you', picks.length)}
          {picks.map((r, i) => <TemplateRow key={'p' + r.template.id} template={r.template} onOpen={onOpen} badge={i === 0 ? 'Best match' : `${r.fit}% fit`} last={i === picks.length - 1} />)}
        </section>
      )}
      {groups.map(({ g, rows }) => (
        <section key={g} aria-label={GOAL_SECTION[g]}>
          {header(GOAL_SECTION[g], rows.length)}
          {rows.map((r, i) => <TemplateRow key={r.template.id} template={r.template} onOpen={onOpen} last={i === rows.length - 1} />)}
        </section>
      ))}
      {list.length === 0 && (
        <div style={{ ...card, padding: 24, textAlign: 'center', marginTop: 12 }}>
          <div style={{ fontFamily: LL.display, fontSize: LL.fs.lg, fontWeight: 700, color: LL.fg }}>No programs match</div>
          <div style={{ ...meta, marginTop: 4, marginBottom: 14 }}>Try fewer filters, or build your own.</div>
          <Btn tone="secondary" size="sm" onClick={clear}>Clear filters</Btn>
        </div>
      )}
      <style>{'.llp-row:active{background:' + LL.surface + ' !important}'}</style>
    </div>
  );
}

export function coachProgramAsk(profile) {
  const p = profile || {};
  const bits = [];
  if (p.daysPerWeek) bits.push(`${p.daysPerWeek} days a week`);
  if (p.sessionMinutes) bits.push(`about ${p.sessionMinutes} minutes per session`);
  if (p.goal) bits.push(`goal: ${GOALS.find(g => g.id === p.goal)?.label.toLowerCase() || p.goal}`);
  if (p.experience) bits.push(`experience: ${p.experience}`);
  const eq = profileEquipment(p);
  if (eq) bits.push(`equipment: ${eq.length ? eq.join(', ') : 'bodyweight only'}`);
  if (p.injuries) bits.push(`limitations: ${p.injuries}`);
  return bits.length
    ? `Build me a program: ${bits.join('; ')}.`
    : 'Build me a program. Ask me about my goal, schedule and equipment first.';
}

// ——— ProgramsScreen ————————————————————————————————————————————————————————

function ProgramsScreen() {
  const { state, actions, setRoute, toast } = useApp();
  const start = useStartTemplate();
  const [menuFor, setMenuFor] = React.useState(null);
  const [confirmDel, setConfirmDel] = React.useState(null);
  const [detail, setDetail] = React.useState(null);
  const [starting, setStarting] = React.useState(false);
  const active = state.programs.find(p => p.id === state.activeProgramId) || null;
  const others = state.programs.filter(p => p.id !== active?.id);
  const profile = state.profile || null;

  const onNewProgram = () => {
    const newId = actions.addProgram({ name: 'My program', author: 'You', weeks: 8, currentWeek: 1, split: 'Custom', days: [
      { id: 'day_' + Math.random().toString(36).slice(2, 8), name: 'Day 1', day: 'Mon', exercises: [] },
    ] });
    setRoute('program-edit', { programId: newId });
  };
  // Starting a library program makes it the active one: ask first when another
  // program is already active (it stays in the library either way).
  const [switchTo, setSwitchTo] = React.useState(null);
  const onStart = async (t, confirmed = false) => {
    if (starting) return;
    const cur = state.programs.find(p => p.id === state.activeProgramId);
    if (cur && !confirmed) { setSwitchTo({ t, cur }); return; }
    setStarting(true);
    try {
      await start(t);
      setDetail(null);
      setRoute('home');
    } finally { setStarting(false); }
  };
  const menuProg = state.programs.find(p => p.id === menuFor);
  // Sharing: '#p=…' links open the preview sheet; pasted links too.
  const [sharing, setSharing] = React.useState(null);
  const [importOpen, setImportOpen] = React.useState(false);
  const [hashToken, clearHash] = useSharedLinkToken();
  const [pastedToken, setPastedToken] = React.useState(null);
  const previewToken = pastedToken || hashToken;
  const closePreview = () => { const fromHash = !pastedToken && !!hashToken; setPastedToken(null); clearHash(); if (fromHash) setRoute('programs'); };
  const onSharedAdded = (id, becameActive) => {
    setPastedToken(null); clearHash();
    if (!state.welcomed) actions.markWelcomed();
    if (becameActive) setRoute('home'); else setRoute('programs');
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative', background: LL.bg }}>
      <ProgramStyles />
      <div style={{ flex: 1, overflow: 'auto', padding: '0 16px 110px' }}>
        <div style={{ paddingTop: LL.pageTop, paddingBottom: 16, display: 'flex', alignItems: 'flex-end', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={kicker}>{state.programs.length ? `${state.programs.length} program${state.programs.length === 1 ? '' : 's'}` : 'Plan your training'}</div>
            <div style={{ fontFamily: LL.display, fontWeight: 700, fontSize: LL.fs.h1, color: LL.fg, letterSpacing: -0.6, lineHeight: 1.05, marginTop: 4 }}>Programs</div>
          </div>
          <IconBtn title="New program" tone="soft" size={44} onClick={onNewProgram}><PIcon.plus style={{ width: 22, height: 22 }} /></IconBtn>
        </div>

        {/* Active program */}
        {active ? (
          <ActiveProgramCard program={active} onEdit={() => setRoute('program-edit', { programId: active.id })} onMenu={() => setMenuFor(active.id)} />
        ) : (
          <div style={{ ...card, padding: 20, display: 'flex', gap: 14, alignItems: 'center' }}>
            <GoalTile goal="hypertrophy" icon={PIcon.layers} size={48} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: LL.display, fontSize: LL.fs.lg, fontWeight: 700, color: LL.fg }}>No program yet</div>
              <div style={{ ...meta, marginTop: 2, lineHeight: 1.4 }}>Pick one from the library below, ask the coach, or build your own.</div>
            </div>
          </div>
        )}

        {others.length > 0 && (
          <div style={{ marginTop: 22 }}>
            <div style={{ ...kicker, margin: '0 2px 10px' }}>Your other programs</div>
            <div style={{ ...card, overflow: 'hidden' }}>
              {others.map((p, i) => (
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 10px 12px 14px', borderTop: i ? `1px solid ${LL.line}` : 'none' }}>
                  <button type="button" onClick={() => setRoute('program-edit', { programId: p.id })} style={{ flex: 1, minWidth: 0, background: 'none', border: 'none', padding: 0, textAlign: 'left', cursor: 'pointer' }}>
                    <div style={{ fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, color: LL.fg, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                    <div style={{ ...meta, fontSize: LL.fs.sm, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{programMeta(p)}</div>
                  </button>
                  <Btn tone="secondary" size="sm" onClick={() => { actions.setActiveProgram(p.id); toast(`${p.name} is now active`); }}>Make active</Btn>
                  <IconBtn title={`${p.name} options`} tone="ghost" size={36} onClick={() => setMenuFor(p.id)}><LLIcon.more /></IconBtn>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* AI coach */}
        <button type="button" onClick={() => setRoute('ai-coach', { ask: coachProgramAsk(profile) })} style={{
          ...card, marginTop: 22, width: '100%', padding: 14, display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left', cursor: 'pointer',
          background: `linear-gradient(120deg, color-mix(in oklab, ${GOAL_HUE.general} 14%, ${LL.surface}), ${LL.surface} 70%)`,
        }}>
          <GoalTile goal="general" icon={PIcon.sparkle} size={44} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, color: LL.fg }}>Generate with AI coach</div>
            <div style={{ ...meta, fontSize: LL.fs.sm, marginTop: 2 }}>A custom plan from your goals, schedule and history</div>
          </div>
          <LLIcon.chev style={{ color: LL.fgFaint }} />
        </button>
        <button type="button" onClick={() => setImportOpen(true)} style={{
          ...card, marginTop: 10, width: '100%', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left', cursor: 'pointer',
        }}>
          <span style={{ width: 44, height: 44, borderRadius: LL.r.md, background: LL.surface2, color: LL.fgMute, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><PIcon.link style={{ width: 20, height: 20 }} /></span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, color: LL.fg }}>Import a shared program</div>
            <div style={{ ...meta, fontSize: LL.fs.sm, marginTop: 2 }}>Paste a link a friend or coach sent you</div>
          </div>
          <LLIcon.chev style={{ color: LL.fgFaint }} />
        </button>

        {/* Library */}
        <div style={{ marginTop: 28 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', margin: '0 2px 12px' }}>
            <div style={{ fontFamily: LL.display, fontWeight: 700, fontSize: LL.fs.h3, color: LL.fg, letterSpacing: -0.3 }}>Program library</div>
            <span style={{ ...meta, fontSize: LL.fs.sm }}>{TEMPLATES.length} programs</span>
          </div>
          <ProgramLibrary onOpen={setDetail} profile={profile} />
        </div>
      </div>

      <TemplateDetailSheet template={detail} open={!!detail} onClose={() => setDetail(null)} onStart={(t) => onStart(t)} starting={starting} profile={profile} />
      <ConfirmDialog
        open={!!switchTo}
        title={switchTo ? `Make ${switchTo.t.name} your active program?` : ''}
        body={switchTo ? `Home will follow it from Day 1. ${switchTo.cur.name} stays in your programs, with its history.` : ''}
        confirmLabel="Switch program"
        onConfirm={() => { const t = switchTo.t; setSwitchTo(null); onStart(t, true); }}
        onCancel={() => setSwitchTo(null)}
      />

      <ActionSheet
        open={!!menuProg} onClose={() => setMenuFor(null)} title={menuProg?.name || ''} subtitle={menuProg ? programMeta(menuProg) : ''}
        items={menuProg ? [
          { id: 'edit', icon: PIcon.edit, label: 'Edit program', onClick: () => { setMenuFor(null); setRoute('program-edit', { programId: menuProg.id }); } },
          menuProg.id !== state.activeProgramId && { id: 'active', icon: LLIcon.check, label: 'Make active', sub: 'Home will follow this program', onClick: () => { actions.setActiveProgram(menuProg.id); toast(`${menuProg.name} is now active`); setMenuFor(null); } },
          { id: 'share', icon: PIcon.share, label: 'Share program', sub: 'Link and QR code, without your weights', onClick: () => { setSharing(menuProg); setMenuFor(null); } },
          { id: 'dup', icon: PIcon.copy, label: 'Duplicate', onClick: () => { actions.duplicateProgram(menuProg.id); setMenuFor(null); } },
          { id: 'del', icon: PIcon.trash, label: 'Delete program', tone: 'danger', onClick: () => { setConfirmDel(menuProg); setMenuFor(null); } },
        ] : []}
      />
      <ShareProgramSheet open={!!sharing} program={sharing} onClose={() => setSharing(null)} />
      <ImportLinkSheet open={importOpen} onClose={() => setImportOpen(false)} onPreview={(t) => { setImportOpen(false); setPastedToken(t); }} />
      <SharedProgramSheet token={previewToken} onClose={closePreview} onAdded={onSharedAdded} />
      <ConfirmDialog
        open={!!confirmDel}
        title={`Delete ${confirmDel?.name || ''}?`}
        body={state.programs.length <= 1
          ? 'This is your only program. Your workout history is kept, and you can still log freestyle workouts.'
          : 'The program and its days are removed. Your workout history is kept.'}
        confirmLabel="Delete" tone="danger"
        onConfirm={() => { if (confirmDel) actions.deleteProgram(confirmDel.id); setConfirmDel(null); }}
        onCancel={() => setConfirmDel(null)}
      />
    </div>
  );
}

function ActiveProgramCard({ program: p, onEdit, onMenu }) {
  const { state } = useApp();
  const days = p.days || [];
  const exCount = days.reduce((a, d) => a + (d.exercises?.length || 0), 0);
  const avgMin = days.length ? Math.round(days.reduce((a, d) => a + estDayMinutes(p, d), 0) / days.length / 5) * 5 : 0;
  const done = (state.sessions || []).filter(s => s.programId === p.id && s.completedAt).length;
  return (
    <div className="llp-anim" style={{
      ...card, padding: 18, position: 'relative', overflow: 'hidden',
      borderColor: `color-mix(in oklab, ${LL.accent} 45%, ${LL.line})`,
      background: `linear-gradient(155deg, color-mix(in oklab, ${LL.accent} 10%, ${LL.surface}), ${LL.surface} 65%)`,
      animation: reducedMotion() ? undefined : `llpFadeUp ${LL.motion.slow}ms ${LL.ease} both`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ width: 8, height: 8, borderRadius: LL.r.xs, background: LL.accent, boxShadow: `0 0 0 4px color-mix(in oklab, ${LL.accent} 22%, transparent)` }} />
        <span style={{ ...kicker, color: LL.accentText }}>Active program</span>
        <div style={{ flex: 1 }} />
        <IconBtn title="Program options" tone="ghost" size={36} onClick={onMenu} style={{ marginRight: -8, marginTop: -6 }}><LLIcon.more /></IconBtn>
      </div>
      <div style={{ fontFamily: LL.display, fontSize: LL.fs.h2, fontWeight: 700, color: LL.fg, letterSpacing: -0.5, lineHeight: 1.08, marginTop: 4, overflowWrap: 'anywhere' }}>{p.name}</div>
      <div style={{ ...meta, marginTop: 4 }}>{programMeta(p)}{done ? ` · ${done} workout${done === 1 ? '' : 's'} done` : ''}</div>
      <ActivePlanLine program={p} sessions={state.sessions} onFix={onEdit} />
      <UpNext state={state} program={p} />
      <div style={{ marginTop: 14 }}><SplitStrip days={days.map(d => d.day).filter(Boolean)} size="lg" /></div>
      <div style={{ display: 'flex', gap: 18, marginTop: 14 }}>
        {[[days.length, 'days'], [exCount, 'exercises'], [avgMin, 'min avg']].map(([v, l]) => (
          <div key={l}>
            <span style={{ fontFamily: LL.display, fontSize: LL.fs.stat, fontWeight: 700, color: LL.fg, fontVariantNumeric: 'tabular-nums' }}>{v}</span>
            <span style={{ ...meta, fontSize: LL.fs.sm, marginLeft: 4 }}>{l}</span>
          </div>
        ))}
      </div>
      <Btn full tone="secondary" onClick={onEdit} icon={<PIcon.edit />} style={{ marginTop: 16 }}>Edit program</Btn>
    </div>
  );
}

// ——— Training plan: weeks, schemes, training maxes ————————————————————————


// Pending training maxes entered in the template sheet, picked up by useStartTemplate
// (works for every caller, including onboarding, without changing its call).
const _pendingTMs = new Map();
const takePendingTMs = (templateId) => { const v = _pendingTMs.get(templateId) || null; _pendingTMs.delete(templateId); return v; };

// One exercise's prescription line for a week. A missing training max is a
// small warn dot, never orange text on every row.
function RxLine({ pe, rx, units, last, showTag = true }) {
  const tm = rx.tm;
  const { simple } = useSimple();
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '9px 0', borderBottom: last ? 'none' : `1px solid ${LL.line}` }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {(rx.needsTM || rx.flagged) && <span aria-label={rx.needsTM ? `Needs a ${simple ? 'working' : 'training'} max` : 'Joint pain flagged'} style={{ width: 6, height: 6, borderRadius: LL.r.xs, background: LL.warn, flexShrink: 0 }} />}
          <span style={{ fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, color: LL.fg, lineHeight: 1.3, overflowWrap: 'anywhere' }}>{pe.name}</span>
          {showTag && <SchemeTag scheme={schemeOf(pe)} />}
        </div>
        <div style={{ ...meta, fontSize: LL.fs.sm, marginTop: 3, lineHeight: 1.45 }}>
          {rowsSummary(rx.rows, units, { weights: !rx.needsTM })}
        </div>
      </div>
      {tm > 0 && <span style={{ ...meta, fontSize: LL.fs.sm, color: LL.fgDim, flexShrink: 0, marginTop: 2 }}>{simple ? 'Max' : 'TM'} {fmtWeight(tm, units)}</span>}
    </div>
  );
}

// The program's plan: where you are, what each week holds, your training maxes.
function PlanCard({ program, units, sessions, onEditTMs, onPinWeek, onRestart }) {
  const { simple } = useSimple();
  const TMW = simple ? 'working max' : 'training max';
  const info = React.useMemo(() => programWeekInfo({ program, sessions }), [program, sessions]);
  const cyc = cycleOf(program);
  const hasWeeks = !!program.cycle || cyc.weeks.length > 1;
  const [sel, setSel] = React.useState(info.week);
  React.useEffect(() => { setSel(info.week); }, [info.week]);
  const lifts = tmLifts(program);
  const missing = lifts.filter(l => !(l.tm > 0));
  const engineDays = (program.days || []).map(d => ({ d, pes: d.exercises.filter(pe => schemeOf(pe) !== 'double') })).filter(x => x.pes.length);
  const wkIndex = info.cycle * cyc.weeks.length + sel;
  const selWeek = cyc.weeks[sel] || {};
  const doneSet = new Set(info.daysDone);
  // One scheme on every engine row → name it once in the header, not per row.
  const engineSchemes = [...new Set(engineDays.flatMap(x => x.pes.map(pe => schemeOf(pe))))];
  const oneScheme = engineSchemes.length === 1 ? engineSchemes[0] : null;
  const headScheme = oneScheme ? (oneScheme === 'percent' ? `% of ${TMW}` : simple && oneScheme === 'rp_sets' ? 'Sets build week by week' : SCHEMES[oneScheme]?.label) : null;

  return (
    <div style={{ ...card, marginTop: 16, padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ ...kicker, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Training plan{headScheme ? ` · ${headScheme}` : ''}</div>
          {hasWeeks ? (
            <>
              <div style={{ fontFamily: LL.display, fontSize: LL.fs.stat, fontWeight: 700, color: LL.fg, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>
                Week {info.week + 1} <span style={{ color: LL.fgDim, fontWeight: 600 }}>of {cyc.weeks.length}</span>
                {cyc.repeat && info.cycle > 0 && <span style={{ ...meta, fontFamily: LL.ui, fontSize: LL.fs.sm, marginLeft: 8 }}>cycle {info.cycle + 1}</span>}
              </div>
              <div style={{ ...meta, marginTop: 2 }}>{info.deload ? 'Deload week — lighter, fewer sets' : weekName(cyc.weeks[info.week], info.week)}{info.finished ? ' · block complete' : ''}</div>
            </>
          ) : (
            <div style={{ fontFamily: LL.display, fontSize: LL.fs.h3, fontWeight: 700, color: LL.fg, marginTop: 4 }}>Progression runs for you</div>
          )}
        </div>
        {info.deload && hasWeeks && <Pill tone="warn">Deload</Pill>}
      </div>

      {/* One calm banner when training maxes are missing. */}
      {missing.length > 0 && (
        <div role="status" style={{
          marginTop: 12, minHeight: 44, display: 'flex', alignItems: 'center', gap: 10, padding: '6px 6px 6px 12px', borderRadius: LL.r.md, boxSizing: 'border-box',
          background: `color-mix(in oklab, ${LL.warn} 12%, ${LL.surface})`, border: `1px solid color-mix(in oklab, ${LL.warn} 40%, transparent)`,
        }}>
          <span style={{ width: 6, height: 6, borderRadius: LL.r.xs, background: LL.warn, flexShrink: 0 }} />
          <span style={{ flex: 1, minWidth: 0, fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 600, color: LL.fg }}>
            {missing.length === lifts.length ? (lifts.length === 1 ? `This lift needs a ${TMW}` : `${lifts.length} lifts need a ${TMW}`) : `${missing.length} of ${lifts.length} lifts need a ${TMW}`}
          </span>
          <button type="button" onClick={onEditTMs} style={{ height: 32, padding: '0 12px', borderRadius: LL.r.sm, border: 'none', background: 'transparent', color: LL.accentText, fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 700, cursor: 'pointer' }}>Set now</button>
        </div>
      )}

      {/* This week's workouts */}
      {(program.days || []).length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
          <div style={{ display: 'flex', gap: 4 }}>
            {program.days.map(d => (
              <span key={d.id} title={d.name} style={{ width: 22, height: 6, borderRadius: LL.r.xs, background: doneSet.has(d.id) ? LL.accent : LL.surface3 }} />
            ))}
          </div>
          <span style={{ ...meta, fontSize: LL.fs.sm }}>{info.daysDone.length} of {program.days.length} workouts done this week</span>
        </div>
      )}

      {hasWeeks && (
        <div style={{ marginTop: 14 }}>
          <WeekStrip weeks={cyc.weeks} current={info.week} selected={sel} onSelect={setSel} />
        </div>
      )}

      {/* Prescriptions for the selected week */}
      {engineDays.length > 0 && (
        <div style={{ marginTop: 10 }}>
          {hasWeeks && sel !== info.week && (
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', margin: '4px 0 2px' }}>
              <span style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 700, color: LL.fg }}>Week {sel + 1} · {selWeek.deload ? 'Deload' : weekName(selWeek, sel)}</span>
              <button type="button" onClick={() => setSel(info.week)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: LL.accentText, fontFamily: LL.ui, fontSize: LL.fs.sm, fontWeight: 600 }}>Back to this week</button>
            </div>
          )}
          {engineDays.map(({ d, pes }) => {
            // A day of mesocycle work collapses to one line: exercises, sets, effort.
            if (pes.length > 2 && pes.every(pe => schemeOf(pe) === 'rp_sets')) {
              const rxs = pes.map(pe => prescribe({ program, pe, weekIndex: wkIndex, units }));
              const sets = rxs.reduce((a, r) => a + r.rows.length, 0);
              const rir = rxs[0].rows[0]?.rir;
              const flagged = rxs.some(r => r.flagged);
              return (
                <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: `1px solid ${LL.line}` }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {flagged && <span aria-label="Joint pain flagged" style={{ width: 6, height: 6, borderRadius: LL.r.xs, background: LL.warn }} />}
                      <span style={{ fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, color: LL.fg }}>{d.name}</span>
                      {!oneScheme && <SchemeTag scheme="rp_sets" />}
                    </div>
                    <div style={{ ...meta, fontSize: LL.fs.sm, marginTop: 3 }}>{pes.length} exercises · {rir === 0 ? 'last set to failure' : `${rir} rep${rir === 1 ? '' : 's'} ${simple ? 'left in the tank' : 'in reserve'}`}{flagged ? ' · joint pain flagged' : ''}</div>
                  </div>
                  <span style={{ fontFamily: LL.display, fontSize: LL.fs.lg, fontWeight: 700, color: LL.fg, fontVariantNumeric: 'tabular-nums' }}>{sets}<span style={{ ...meta, fontSize: LL.fs.sm, marginLeft: 3 }}>sets</span></span>
                </div>
              );
            }
            return (
              <div key={d.id} style={{ marginTop: 8 }}>
                <div style={{ ...kicker, color: LL.fgDim }}>{d.name}</div>
                {pes.map((pe, i) => (
                  <RxLine key={pe.id} pe={pe} units={units} last={i === pes.length - 1} showTag={!oneScheme}
                    rx={prescribe({ program, pe, weekIndex: wkIndex, units, history: sessions })} />
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* Training maxes */}
      {lifts.some(l => l.tm > 0) && (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${LL.line}` }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <div style={{ ...kicker, display: 'flex', alignItems: 'center', gap: 6 }}>{simple ? 'Working maxes' : 'Training maxes'}<Explain id="working-max" /></div>
            <button type="button" onClick={onEditTMs} style={{ background: 'none', border: 'none', padding: '4px 0', cursor: 'pointer', color: LL.accentText, fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 600 }}>Edit</button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
            {lifts.filter(l => l.tm > 0).map(l => (
              <Pill key={l.key}>{l.name} <span style={{ color: LL.fg, fontVariantNumeric: 'tabular-nums' }}>{fmtWeight(l.tm, units)} {unitLabel(units)}</span></Pill>
            ))}
          </div>
        </div>
      )}

      {hasWeeks && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14 }}>
          {sel !== info.week && <Btn tone="secondary" size="sm" onClick={() => onPinWeek(info.index + (sel - info.week))}>Make week {sel + 1} current</Btn>}
          <Btn tone="ghost" size="sm" onClick={onRestart} style={{ paddingLeft: 4 }}>Restart from week 1</Btn>
        </div>
      )}
    </div>
  );
}

// "Set your training maxes": enter a recent best set per lift; TM = 90 % of its e1RM.
function TrainingMaxForm({ lifts, units, values, onChange }) {
  const ul = unitLabel(units);
  const { simple } = useSimple();
  const inputStyle = {
    width: '100%', height: 44, padding: '0 12px', boxSizing: 'border-box', borderRadius: LL.r.md,
    background: LL.surface2, color: LL.fg, border: `1px solid ${LL.line}`, outline: 'none',
    fontFamily: LL.ui, fontSize: LL.fs.body, fontVariantNumeric: 'tabular-nums',
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {lifts.map(l => {
        const v = values[l.key] || {};
        const w = parseFloat(v.w), r = parseInt(v.r, 10);
        const fromSet = w > 0 && r > 0 && r <= 12 ? trainingMaxFrom(units === 'kg' ? w / 0.45359237 : w, r, units) : 0;
        const tm = fromSet || l.tm || 0;
        return (
          <div key={l.key} style={{ ...card, padding: 12 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
              <span style={{ fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, color: LL.fg }}>{l.name}</span>
              <span style={{ fontFamily: LL.display, fontSize: LL.fs.lg, fontWeight: 700, color: tm > 0 ? LL.fg : LL.fgFaint, fontVariantNumeric: 'tabular-nums' }}>
                {tm > 0 ? `${fmtWeight(tm, units)} ${ul}` : '—'}
              </span>
            </div>
            <div style={{ ...meta, fontSize: LL.fs.sm, marginTop: 2 }}>
              {fromSet ? (simple ? 'Working max = 90% of the most you could lift once, worked out from that set' : 'Training max = 90% of that set’s estimated 1RM') : l.tm > 0 ? 'From your recent sessions — enter a set to change it' : 'Your best recent set, done with good form'}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 8, marginTop: 10 }}>
              <input inputMode="decimal" aria-label={`${l.name} weight`} placeholder={`Weight (${ul})`} value={v.w || ''} onChange={(e) => onChange(l.key, { ...v, w: e.target.value.replace(/[^0-9.]/g, '') })} style={inputStyle} />
              <span style={{ ...meta }}>×</span>
              <input inputMode="numeric" aria-label={`${l.name} reps`} placeholder="Reps" value={v.r || ''} onChange={(e) => onChange(l.key, { ...v, r: e.target.value.replace(/[^0-9]/g, '') })} style={inputStyle} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

const tmsFromForm = (lifts, values, units) => {
  const out = {};
  for (const l of lifts) {
    const v = values[l.key] || {};
    const w = parseFloat(v.w), r = parseInt(v.r, 10);
    const tm = w > 0 && r > 0 && r <= 12 ? trainingMaxFrom(units === 'kg' ? w / 0.45359237 : w, r, units) : l.tm;
    if (tm > 0) out[l.key] = tm;
  }
  return out;
};

function TrainingMaxSheet({ open, onClose, program, units, onSave }) {
  const lifts = React.useMemo(() => (program ? tmLifts(program) : []), [program]);
  const [values, setValues] = React.useState({});
  React.useEffect(() => { if (open) setValues({}); }, [open]);
  const ready = tmsFromForm(lifts, values, units);
  const count = Object.keys(ready).length;
  const { simple } = useSimple();
  return (
    <Sheet open={open} onClose={onClose} title={simple ? 'Working maxes' : 'Training maxes'} subtitle="Percentages in this program are of these numbers"
      footer={<Btn full size="lg" disabled={!count} onClick={() => onSave(ready)}>{count < lifts.length ? `Save ${count} of ${lifts.length}` : `Save ${simple ? 'working' : 'training'} maxes`}</Btn>}>
      <TrainingMaxForm lifts={lifts} units={units} values={values} onChange={(k, v) => setValues(s => ({ ...s, [k]: v }))} />
      <div style={{ ...meta, fontSize: LL.fs.sm, color: LL.fgDim, marginTop: 12, lineHeight: 1.45 }}>
        {simple ? 'A working max' : 'A training max'} a little under your true max keeps every set crisp and leaves room to progress. Not sure? Use a set of 3–5 you could do today.
      </div>
    </Sheet>
  );
}

// Template exercise → a short line describing its scheme (no TM needed).
function templateExerciseLine(e, t, simple = false) {
  const s = e.scheme || 'double';
  if (s === 'double') return null;
  const pe = { scheme: s, schemeCfg: e.cfg, sets: e.sets, repMin: e.repMin, repMax: e.repMax, group: '' };
  const rx = prescribe({ program: { cycle: t.cycle, days: [] }, pe, weekIndex: 0 });
  if (s === 'percent') {
    const wave = WAVES[e.cfg.wave];
    const pcts = wave.weeks.flat().map(x => x.pct);
    return `${wave.label} · ${Math.min(...pcts)}–${Math.max(...pcts)}% ${simple ? 'of max' : 'TM'}`;
  }
  if (s === 'rpe_topset' && simple) return `One heavy set of ${e.cfg.topReps}, then ${e.cfg.backoffs}×${e.cfg.backoffReps} lighter`;
  if (s === 'gzcl' && simple) return `${['', 'Heavy', 'Medium', 'Light'][e.cfg.tier] || 'Tier ' + e.cfg.tier} · ${rowsSummary(rx.rows, 'lb', { weights: false })}`;
  if (s === 'rp_sets' && simple) return `${e.cfg.mev}→${e.cfg.mrv} sets × ${fmtReps(e.repMin, e.repMax)} · builds week by week`;
  if (s === 'rpe_topset') return `Top ${e.cfg.topReps} @ RPE ${Array.isArray(e.cfg.rpe) ? e.cfg.rpe.slice(0, -1).join('→') : e.cfg.rpe} · ${e.cfg.backoffs}×${e.cfg.backoffReps} back-offs`;
  if (s === 'gzcl') return `Tier ${e.cfg.tier} · ${rowsSummary(rx.rows, 'lb', { weights: false })}`;
  if (s === 'rp_sets') return `${e.cfg.mev}→${e.cfg.mrv} sets × ${fmtReps(e.repMin, e.repMax)} · RIR 3→0`;
  if (s === 'linear') return `${e.sets}×${e.repMin} · +${e.cfg.inc || 5} lb a session`;
  return null;
}

// Week-by-week preview for a template (percent / RPE / meso schemes).
function TemplateWeeks({ t }) {
  const [sel, setSel] = React.useState(0);
  if (!t.cycle) return null;
  const weeks = t.cycle.weeks;
  const prog = { cycle: t.cycle, days: [] };
  // One line per distinct scheme/wave, naming the lifts that run it.
  const groups = new Map();
  for (const d of t.days) for (const e of d.exercises) {
    if (!e.scheme || e.scheme === 'double') continue;
    const key = e.scheme + '|' + (e.cfg?.wave || '') + '|' + (e.scheme === 'rp_sets' ? `${e.cfg.mev}-${e.cfg.mrv}` : e.scheme === 'rpe_topset' ? `${e.cfg.topReps}-${e.cfg.backoffs}-${e.cfg.backoffReps}` : '');
    if (!groups.has(key)) groups.set(key, { e, names: [] });
    const g = groups.get(key);
    const short = e.x.replace(/^(Barbell|Conventional|Back) /, '').replace(/ \(.*\)$/, '');
    if (!g.names.includes(short)) g.names.push(short);
  }
  const rows = [...groups.values()].slice(0, 5).map(({ e, names }, i) => {
    const pe = { id: 'g' + i, name: names.length > 3 ? `${names.slice(0, 3).join(', ')} +${names.length - 3}` : names.join(', '), scheme: e.scheme, schemeCfg: e.cfg, sets: e.sets, repMin: e.repMin, repMax: e.repMax, group: '' };
    return { pe, rx: prescribe({ program: prog, pe, weekIndex: sel }) };
  });
  return (
    <div style={{ marginTop: 14, ...card, padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={kicker}>Week by week</div>
        <span style={{ ...meta, fontSize: LL.fs.sm }}>{weeks.length} weeks{t.cycle.repeat ? ', repeats' : ''}{weeks.some(w => w.deload) ? ` · ${weeks.filter(w => w.deload).length} deload` : ''}</span>
      </div>
      <WeekStrip weeks={weeks} current={-1} selected={sel} onSelect={setSel} />
      <div style={{ marginTop: 6 }}>
        {rows.map(({ pe, rx }, i) => <RxLine key={pe.id} pe={pe} rx={{ ...rx, needsTM: false, tm: 0 }} units="lb" last={i === rows.length - 1} />)}
      </div>
    </div>
  );
}

// "Up next" on the active program card: the same answer as Home (nextWorkout).
function UpNext({ state, program }) {
  const nx = nextWorkout(state, program.id);
  if (!nx.day) return null;
  const when = nx.active ? 'in progress' : nx.today ? 'today' : nx.plannedFor ? `planned for ${nx.plannedFor}` : null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, padding: '10px 12px', borderRadius: LL.r.md, background: LL.surface2, border: `1px solid ${LL.line}` }}>
      <span style={{ ...kicker, color: LL.accentText, flexShrink: 0 }}>{nx.active ? 'Now' : 'Up next'}</span>
      <span style={{ flex: 1, minWidth: 0, fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, color: LL.fg, overflowWrap: 'anywhere' }}>{(nx.active && program.days.find(d => d.id === nx.active.dayId)?.name) || nx.day.name}</span>
      {when && <span style={{ ...meta, fontSize: LL.fs.sm, flexShrink: 0 }}>{when}</span>}
    </div>
  );
}

// Current week + training-max status, shown on the active program card.
function ActivePlanLine({ program, sessions, onFix }) {
  const { simple } = useSimple();
  const cyc = cycleOf(program);
  const hasWeeks = !!program.cycle || cyc.weeks.length > 1;
  const miss = missingTMs(program).length;
  if (!hasWeeks && !miss) return null;
  const info = nextWorkout({ programs: [program], activeProgramId: program.id, sessions });
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginTop: 10 }}>
      {hasWeeks && <Pill tone={info.deload ? 'warn' : 'accent'}>Week {info.week + 1} of {cyc.weeks.length}{info.deload ? (simple ? ' · lighter week' : ' · deload') : ''}</Pill>}
      {hasWeeks && !info.deload && <span style={{ ...meta, fontSize: LL.fs.sm }}>{weekName(cyc.weeks[info.week], info.week)}</span>}
      {miss > 0 && (
        <button type="button" onClick={onFix} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, color: `color-mix(in oklab, ${LL.warn} 70%, ${LL.fg})`, fontFamily: LL.ui, fontSize: LL.fs.sm, fontWeight: 600 }}>
          <PIcon.warn style={{ width: 14, height: 14 }} /> Set {simple ? 'working' : 'training'} maxes
        </button>
      )}
    </div>
  );
}

// ——— ProgramEditor —————————————————————————————————————————————————————————

const PRESET_ICON = { wave531: PIcon.wave, meso: PIcon.bars, linear: PIcon.stairs };

// The program name, edited in place: 28px, wraps to as many lines as it needs
// (auto-sized from its scroll height) instead of clipping or reserving a line.
function ProgramNameField({ value, onChange }) {
  const ref = React.useRef(null);
  const fit = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);
  React.useLayoutEffect(fit, [value, fit]);
  React.useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(fit);
    ro.observe(el.parentElement || el);
    return () => ro.disconnect();
  }, [fit]);
  return (
    <textarea
      ref={ref} rows={1} aria-label="Program name" value={value} placeholder="Program name"
      onChange={(e) => onChange(e.target.value.replace(/\s*\n\s*/g, ' '))}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
      style={{
        display: 'block', width: '100%', boxSizing: 'border-box', margin: 0, padding: 0, border: 'none', background: 'transparent',
        resize: 'none', overflow: 'hidden', outline: 'none', overflowWrap: 'anywhere',
        fontFamily: LL.display, fontWeight: 700, fontSize: LL.fs.h2, lineHeight: 1.1, letterSpacing: -0.4, color: LL.fg,
      }}
    />
  );
}


function ProgramEditor({ programId }) {
  const { state, actions, goBack, replaceRoute, toast, route } = useApp();
  const [tmOpen, setTmOpen] = React.useState(() => !!route?.params?.setTMs);
  const catalog = useCatalog();
  const [expandedDay, setExpandedDay] = React.useState(() => {
    const p = state.programs.find(x => x.id === programId);
    return p?.days?.[0]?.id || null;
  });
  const [expandedEx, setExpandedEx] = React.useState(null);
  const [dayMenu, setDayMenu] = React.useState(null);
  const [progMenu, setProgMenu] = React.useState(false);
  const [confirmDeleteDay, setConfirmDeleteDay] = React.useState(null);
  const [confirmDeleteEx, setConfirmDeleteEx] = React.useState(null);
  const [confirmDeleteProgram, setConfirmDeleteProgram] = React.useState(false);
  const [pickerForDay, setPickerForDay] = React.useState(null);
  const [renameFocus, setRenameFocus] = React.useState(null);
  const [sharing, setSharing] = React.useState(false);
  const [preset, setPreset] = React.useState(null); // { id, label, result }
  const units = state.prefs?.units || 'lb';

  const program = state.programs.find(p => p.id === programId);
  if (!program) {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 24, background: LL.bg }}>
        <div style={{ fontFamily: LL.display, fontSize: LL.fs.h3, fontWeight: 700, color: LL.fg }}>Program not found</div>
        <div style={meta}>It may have been deleted.</div>
        <Btn tone="secondary" onClick={() => replaceRoute('programs')}>Back to programs</Btn>
      </div>
    );
  }

  const isActive = program.id === state.activeProgramId;
  const patch = (p) => actions.updateProgram(program.id, p);
  const days = program.days || [];
  const setDays = (next) => patch({ days: next });
  const moveDay = (idx, dir) => {
    const j = idx + dir; if (j < 0 || j >= days.length) return;
    const next = [...days]; const [m] = next.splice(idx, 1); next.splice(j, 0, m); setDays(next);
  };
  const duplicateDay = (idx) => {
    const d = days[idx]; if (!d) return;
    const rid = (p) => p + Math.random().toString(36).slice(2, 8);
    const ssMap = {};
    const copy = { ...JSON.parse(JSON.stringify(d)), id: rid('day_'), name: `${d.name} (copy)`, day: '' };
    copy.exercises = copy.exercises.map(e => ({ ...e, id: rid('pe_'), ...(e.supersetId ? { supersetId: ssMap[e.supersetId] || (ssMap[e.supersetId] = rid('ss_')) } : {}) }));
    const next = [...days]; next.splice(idx + 1, 0, copy); setDays(next);
    setExpandedDay(copy.id);
    toast('Day duplicated');
  };
  const addDay = () => {
    const used = new Set(days.map(d => d.day));
    const free = WEEK.find(d => !used.has(d)) || '';
    const id = 'day_' + Math.random().toString(36).slice(2, 8);
    setDays([...days, { id, name: `Day ${days.length + 1}`, day: free, exercises: [] }]);
    setExpandedDay(id);
  };
  const dayForMenu = days.find(d => d.id === dayMenu);
  const dayMenuIdx = days.findIndex(d => d.id === dayMenu);
  const totalEx = days.reduce((a, d) => a + d.exercises.length, 0);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative', background: LL.bg }}>
      <ProgramStyles />
      <div style={{ flex: 1, overflow: 'auto', padding: '0 16px 40px' }}>
        {/* Header: circle back + the editable name inline (wraps, never clips), meta below. */}
        <PageHeader onBack={goBack} style={{ padding: '0 0 4px', paddingTop: LL.pageTop, alignItems: 'flex-start' }}
          title={<ProgramNameField value={program.name} onChange={(v) => patch({ name: v })} />}
          titleStyle={{ whiteSpace: 'normal', overflow: 'visible', paddingTop: 4 }}
          right={<IconBtn title="Program options" tone="ghost" onClick={() => setProgMenu(true)} style={{ borderRadius: LL.r.pill }}><LLIcon.more /></IconBtn>} />
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px 10px', marginTop: 2 }}>
          {isActive ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, ...meta, color: LL.accentText, fontWeight: 600 }}>
              <span style={{ width: 8, height: 8, borderRadius: LL.r.xs, background: LL.accent }} /> Active
            </span>
          ) : null}
          <span style={{ ...meta }}>{programMeta(program)} · {totalEx} exercises</span>
          {!isActive && <Btn tone="soft" size="sm" onClick={() => { actions.setActiveProgram(program.id); toast(`${program.name} is now active`); }} style={{ height: 32 }}>Make active</Btn>}
        </div>

        {/* Details */}
        <div style={{ ...card, marginTop: 16, overflow: 'hidden' }}>
          <Row label="Length">
            <NumField value={program.weeks || 8} onChange={(v) => patch({ weeks: v })} min={1} max={52} step={1} suffix={(program.weeks || 8) === 1 ? 'week' : 'weeks'} width={168} ariaLabel="Program length in weeks" decimals={0} />
          </Row>
          <Row label="Split" border grow={false}>
            <input value={splitLabel(program) || ''} onChange={(e) => patch({ split: e.target.value })} placeholder="e.g. Upper / Lower" aria-label="Split name" title={splitLabel(program) || ''} style={{
              flex: 1, minWidth: 0, height: 38, padding: '0 12px', boxSizing: 'border-box', textAlign: 'right', textOverflow: 'ellipsis',
              background: LL.surface2, color: LL.fg, border: `1px solid ${LL.line}`, borderRadius: LL.r.sm,
              fontFamily: LL.ui, fontSize: LL.fs.btn, outline: 'none',
            }} />
          </Row>
          {program.progressionNote && (
            <div style={{ padding: '12px 14px', borderTop: `1px solid ${LL.line}` }}>
              <div style={{ ...kicker, marginBottom: 4 }}>How to progress</div>
              <div style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgMute, lineHeight: 1.45 }}>{program.progressionNote}</div>
            </div>
          )}
        </div>

        {/* Cycle builder: weeks, deload, repeat or end */}
        <CycleCard program={program} sessions={state.sessions} onChange={(p) => patch(p)} />

        {/* Training plan: weeks, schemes, training maxes */}
        {(program.cycle || program.days.some(d => d.exercises.some(pe => schemeOf(pe) !== 'double'))) && (
          <PlanCard program={program} units={units} sessions={state.sessions}
            onEditTMs={() => setTmOpen(true)}
            onPinWeek={(i) => { patch({ weekOffset: withCurrentWeek(program, state.sessions, i).weekOffset }); toast(`Week ${(i % cycleOf(program).weeks.length) + 1} is now current`); }}
            onRestart={() => { patch(restartPlan(program)); toast('Plan restarted at week 1'); }} />
        )}
        <TrainingMaxSheet open={tmOpen} onClose={() => setTmOpen(false)} program={program} units={units}
          onSave={(tms) => { patch({ tms: withTrainingMaxes(program, tms).tms }); setTmOpen(false); toast('Training maxes saved'); }} />

        {/* Periodized weeks (imported programs) */}
        {(!program.cycle && program.periodization?.weeks?.length > 0) && (
          <div style={{ marginTop: 18 }}>
            <div style={{ ...kicker, margin: '0 2px 8px' }}>Current week</div>
            <div className="llp-noscroll" style={{ display: 'flex', gap: 8, overflowX: 'auto', scrollbarWidth: 'none' }}>
              {program.periodization.weeks.map((wk, i) => (
                <FilterChip key={(wk.label || 'w') + i} active={i === (program.periodization.currentWeek ?? 0)} onClick={() => actions.setCurrentWeek(program.id, i)}>
                  {wk.label || `Week ${i + 1}`}
                </FilterChip>
              ))}
            </div>
          </div>
        )}

        {/* Days */}
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', margin: '24px 2px 10px' }}>
          <div style={{ fontFamily: LL.display, fontWeight: 700, fontSize: LL.fs.h3, color: LL.fg }}>Workouts</div>
          <SplitStrip days={days.map(d => d.day).filter(Boolean)} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {days.map((day, idx) => (
            <DayCard
              key={day.id}
              program={program}
              day={day}
              units={units}
              catalog={catalog}
              exercises={state.exercises}
              actions={actions}
              expanded={expandedDay === day.id}
              onToggle={() => setExpandedDay(expandedDay === day.id ? null : day.id)}
              expandedEx={expandedEx}
              setExpandedEx={setExpandedEx}
              onMenu={() => setDayMenu(day.id)}
              onAdd={() => setPickerForDay(day.id)}
              onDeleteExercise={(pe) => setConfirmDeleteEx({ dayId: day.id, peId: pe.id, name: pe.name })}
              focusName={renameFocus === day.id}
              onFocused={() => setRenameFocus(null)}
              takenDays={days.filter(d => d.id !== day.id).map(d => d.day)}
            />
          ))}
          {days.length === 0 && (
            <div style={{ ...card, padding: 24, textAlign: 'center' }}>
              <div style={{ fontFamily: LL.display, fontSize: LL.fs.lg, fontWeight: 700, color: LL.fg }}>No workouts yet</div>
              <div style={{ ...meta, marginTop: 4 }}>Add a day, then add exercises to it.</div>
            </div>
          )}
        </div>
        <button type="button" onClick={addDay} style={{
          marginTop: 10, width: '100%', height: 52, borderRadius: LL.r.md,
          background: 'transparent', border: `1.5px dashed ${LL.line2}`, color: LL.fg,
          fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer',
        }}><PIcon.plus /> Add workout day</button>

        <div style={{ marginTop: 36, display: 'flex', justifyContent: 'center' }}>
          <Btn tone="danger" size="sm" icon={<PIcon.trash />} onClick={() => setConfirmDeleteProgram(true)}>Delete program</Btn>
        </div>
      </div>

      <ActionSheet
        open={progMenu} onClose={() => setProgMenu(false)} title={program.name} subtitle="Program"
        items={[
          !isActive && { id: 'active', icon: LLIcon.check, label: 'Make active', sub: 'Home will follow this program', onClick: () => { actions.setActiveProgram(program.id); toast(`${program.name} is now active`); setProgMenu(false); } },
          { id: 'share', icon: PIcon.share, label: 'Share program', sub: 'Link and QR code, without your weights', onClick: () => { setProgMenu(false); setSharing(true); } },
          ...PRESETS.map(pr => ({ id: 'pre-' + pr.id, icon: PRESET_ICON[pr.id], label: pr.label, sub: pr.sub, onClick: () => {
            setProgMenu(false);
            setPreset({ ...pr, result: applyPreset(program, pr.id, { exercises: state.exercises }) });
          } })),
          { id: 'dup', icon: PIcon.copy, label: 'Duplicate program', onClick: () => { actions.duplicateProgram(program.id); setProgMenu(false); } },
          { id: 'del', icon: PIcon.trash, label: 'Delete program', tone: 'danger', onClick: () => { setProgMenu(false); setConfirmDeleteProgram(true); } },
        ]}
      />
      <ShareProgramSheet open={sharing} program={program} onClose={() => setSharing(false)} />
      <ConfirmDialog
        open={!!preset}
        title={preset ? `${preset.label}?` : ''}
        body={preset ? `${preset.result.summary} Days, names and order stay; you can undo right after.` : ''}
        confirmLabel="Convert"
        onConfirm={() => {
          const before = { days: program.days, cycle: program.cycle, weekOffset: program.weekOffset };
          const next = preset.result.program;
          patch({ days: next.days, ...cyclePatch(program, state.sessions, next.cycle || null) });
          const needTMs = missingTMs(next).length > 0;
          setPreset(null);
          toast(needTMs ? 'Converted · set your training maxes' : 'Program converted', 'default', { action: { label: 'Undo', run: () => patch(before) } });
          if (needTMs) setTmOpen(true);
        }}
        onCancel={() => setPreset(null)}
      />

      <ActionSheet
        open={!!dayForMenu} onClose={() => setDayMenu(null)} title={dayForMenu?.name || ''} subtitle={dayForMenu ? `${dayForMenu.exercises.length} exercises` : ''}
        items={dayForMenu ? [
          { id: 'rename', icon: PIcon.edit, label: 'Rename', onClick: () => { setExpandedDay(dayForMenu.id); setRenameFocus(dayForMenu.id); setDayMenu(null); } },
          { id: 'up', icon: PIcon.up, label: 'Move up', disabled: dayMenuIdx <= 0, onClick: () => { moveDay(dayMenuIdx, -1); setDayMenu(null); } },
          { id: 'down', icon: PIcon.down, label: 'Move down', disabled: dayMenuIdx >= days.length - 1, onClick: () => { moveDay(dayMenuIdx, 1); setDayMenu(null); } },
          { id: 'dup', icon: PIcon.copy, label: 'Duplicate day', onClick: () => { duplicateDay(dayMenuIdx); setDayMenu(null); } },
          { id: 'del', icon: PIcon.trash, label: 'Delete day', tone: 'danger', onClick: () => { setConfirmDeleteDay(dayForMenu); setDayMenu(null); } },
        ] : []}
      />

      <ExercisePicker
        open={!!pickerForDay}
        onClose={() => setPickerForDay(null)}
        multi
        title="Add exercises"
        subtitle={days.find(d => d.id === pickerForDay)?.name}
        onPick={(exs) => {
          const dayId = pickerForDay;
          (exs || []).forEach(ex => actions.addExerciseToDay(program.id, dayId, ex.id));
          setPickerForDay(null);
          setExpandedDay(dayId);
          if (exs && exs.length) toast(exs.length === 1 ? `${exs[0].name} added` : `${exs.length} exercises added`);
        }}
      />

      <ConfirmDialog
        open={!!confirmDeleteDay}
        title={`Delete ${confirmDeleteDay?.name || 'day'}?`}
        body="The day and its exercises are removed from this program. Logged workouts are kept."
        confirmLabel="Delete day" tone="danger"
        onConfirm={() => { if (confirmDeleteDay) actions.deleteDay(program.id, confirmDeleteDay.id); setConfirmDeleteDay(null); }}
        onCancel={() => setConfirmDeleteDay(null)}
      />
      <ConfirmDialog
        open={!!confirmDeleteEx}
        title={`Remove ${confirmDeleteEx?.name || 'exercise'}?`}
        body="It's removed from this day only. Its history stays in your log."
        confirmLabel="Remove" tone="danger"
        onConfirm={() => {
          if (confirmDeleteEx) {
            const d = days.find(x => x.id === confirmDeleteEx.dayId);
            const pe = d?.exercises.find(e => e.id === confirmDeleteEx.peId);
            // Removing one half of a superset pair dissolves the pair.
            if (pe?.supersetId) {
              const rest = d.exercises.filter(e => e.id !== pe.id && e.supersetId === pe.supersetId);
              if (rest.length === 1) actions.updateExerciseInDay(program.id, d.id, rest[0].id, { supersetId: null });
            }
            actions.removeExerciseFromDay(program.id, confirmDeleteEx.dayId, confirmDeleteEx.peId);
          }
          setConfirmDeleteEx(null);
        }}
        onCancel={() => setConfirmDeleteEx(null)}
      />
      <ConfirmDialog
        open={confirmDeleteProgram}
        title={`Delete ${program.name}?`}
        body={state.programs.length <= 1
          ? 'This is your only program. Your workout history is kept, and you can still log freestyle workouts.'
          : 'The program and its days are removed. Your workout history is kept.'}
        confirmLabel="Delete program" tone="danger"
        onConfirm={() => { actions.deleteProgram(program.id); setConfirmDeleteProgram(false); replaceRoute('programs'); }}
        onCancel={() => setConfirmDeleteProgram(false)}
      />
    </div>
  );
}

// ——— Day card ——————————————————————————————————————————————————————————————

function DayCard({ program, day, units, catalog, exercises, actions, expanded, onToggle, expandedEx, setExpandedEx, onMenu, onAdd, onDeleteExercise, focusName, onFocused, takenDays }) {
  const [pickingDay, setPickingDay] = React.useState(false);
  const nameRef = React.useRef(null);
  React.useEffect(() => {
    if (focusName && nameRef.current) { nameRef.current.focus(); nameRef.current.select(); onFocused && onFocused(); }
  }, [focusName, onFocused]);

  // Pointer-driven reorder. Rows swap when the dragged row crosses half a row.
  // Move/up listeners live on window so a re-ordered (re-parented) row can't
  // strand the drag if pointer capture is lost.
  const listRef = React.useRef(null);
  const dragRef = React.useRef(null);
  const [drag, setDrag] = React.useState(null); // { idx, dy }
  const countRef = React.useRef(day.exercises.length);
  countRef.current = day.exercises.length;
  const endDrag = React.useCallback(() => {
    const d = dragRef.current;
    if (d) { window.removeEventListener('pointermove', d.onMove); window.removeEventListener('pointerup', d.onEnd); window.removeEventListener('pointercancel', d.onEnd); }
    dragRef.current = null;
    setDrag(null);
  }, []);
  React.useEffect(() => endDrag, [endDrag]);
  const startDrag = (e, idx) => {
    e.preventDefault(); e.stopPropagation();
    try { window.getSelection && window.getSelection().removeAllRanges(); } catch { /* noop */ }
    setExpandedEx(null);
    const rows = listRef.current ? Array.from(listRef.current.querySelectorAll('[data-pe-row]')) : [];
    const h = rows[idx] ? rows[idx].getBoundingClientRect().height + 6 : 64;
    const d = { startY: e.clientY, cur: idx, h: Math.min(h, 90) };
    d.onMove = (ev) => {
      let dy = ev.clientY - d.startY;
      const steps = Math.trunc(dy / d.h + (dy > 0 ? 0.5 : -0.5));
      const target = Math.max(0, Math.min(countRef.current - 1, d.cur + steps));
      if (target !== d.cur) {
        actions.reorderExercises(program.id, day.id, d.cur, target);
        d.startY += (target - d.cur) * d.h;
        d.cur = target;
        dy = ev.clientY - d.startY;
        try { navigator.vibrate && navigator.vibrate(6); } catch { /* noop */ }
      }
      setDrag({ idx: d.cur, dy });
    };
    d.onEnd = () => endDrag();
    dragRef.current = d;
    window.addEventListener('pointermove', d.onMove);
    window.addEventListener('pointerup', d.onEnd);
    window.addEventListener('pointercancel', d.onEnd);
    setDrag({ idx, dy: 0 });
  };

  const mins = estDayMinutes(program, day);
  const n = day.exercises.length;
  const setsTotal = day.exercises.reduce((a, pe) => a + (prescriptionFor(program, pe).sets ?? pe.sets ?? 0), 0);

  return (
    <div style={{ ...card, overflow: 'hidden', borderColor: expanded ? LL.line2 : LL.line, transition: `border-color ${LL.motion.base}ms ${LL.ease}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 8px 12px 12px' }}>
        <button type="button" onClick={() => setPickingDay(v => !v)} aria-label={`Weekday: ${day.day || 'none'}`} aria-expanded={pickingDay} style={{
          width: 46, height: 46, borderRadius: LL.r.md, flexShrink: 0, cursor: 'pointer', padding: 0,
          background: day.day ? LL.accentSoft : LL.surface2,
          border: `1px ${day.day ? 'solid' : 'dashed'} ${day.day ? `color-mix(in oklab, ${LL.accent} 45%, transparent)` : LL.line2}`,
          color: day.day ? LL.accentText : LL.fgDim,
          fontFamily: LL.ui, fontSize: LL.fs.sm, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1,
        }}>
          {day.day || <PIcon.calendar />}
        </button>
        <div style={{ flex: 1, minWidth: 0 }} onClick={() => { if (!expanded) onToggle(); }}>
          {expanded ? (
            <input
              ref={nameRef}
              aria-label="Day name"
              value={day.name}
              onChange={(e) => actions.updateDay(program.id, day.id, { name: e.target.value })}
              placeholder="Day name"
              style={{
                width: '100%', boxSizing: 'border-box', height: 36, padding: '0 10px', marginLeft: -10,
                background: 'transparent', color: LL.fg, border: `1px solid transparent`, borderRadius: LL.r.sm,
                fontFamily: LL.display, fontSize: LL.fs.lg, fontWeight: 700, outline: 'none',
              }}
              onFocus={(e) => { e.target.style.background = LL.surface2; e.target.style.borderColor = LL.line; }}
              onBlur={(e) => { e.target.style.background = 'transparent'; e.target.style.borderColor = 'transparent'; }}
            />
          ) : (
            <div style={{ fontFamily: LL.display, fontSize: LL.fs.lg, fontWeight: 700, color: LL.fg, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer' }}>{day.name || 'Untitled day'}</div>
          )}
          <div style={{ ...meta, fontSize: LL.fs.sm, marginTop: expanded ? -2 : 2 }}>
            {n ? `${n} exercise${n === 1 ? '' : 's'} · ${setsTotal} sets · ~${mins} min` : 'No exercises yet'}
          </div>
        </div>
        <IconBtn title={`${day.name} options`} tone="ghost" size={38} onClick={onMenu}><LLIcon.more /></IconBtn>
        <IconBtn title={expanded ? 'Collapse' : 'Expand'} tone="ghost" size={38} onClick={onToggle}>
          <LLIcon.chevDown style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: `transform ${LL.motion.base}ms ${LL.ease}` }} />
        </IconBtn>
      </div>

      {pickingDay && (
        <div className="llp-anim" style={{ display: 'flex', gap: 6, padding: '0 12px 12px', flexWrap: 'wrap', animation: reducedMotion() ? undefined : `llpFadeUp ${LL.motion.base}ms ${LL.ease} both` }}>
          {[...WEEK, ''].map(d => {
            const on = (day.day || '') === d;
            const taken = d && takenDays.includes(d);
            return (
              <button key={d || 'none'} type="button" onClick={() => { actions.updateDay(program.id, day.id, { day: d }); setPickingDay(false); }} style={{
                height: 36, minWidth: d ? 42 : 58, padding: '0 8px', borderRadius: LL.r.md, cursor: 'pointer',
                background: on ? LL.accent : LL.surface2, color: on ? LL.accentInk : (taken ? LL.fgDim : LL.fg),
                border: `1px solid ${on ? 'transparent' : LL.line}`,
                fontFamily: LL.ui, fontSize: LL.fs.sm, fontWeight: 600,
              }}>{d || 'Any day'}</button>
            );
          })}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateRows: expanded ? '1fr' : '0fr', transition: `grid-template-rows ${reducedMotion() ? 0 : 260}ms ${LL.ease}` }}>
        <div style={{ overflow: 'hidden' }}>
          <div style={{ borderTop: `1px solid ${LL.line}`, padding: '10px 10px 12px' }}>
            <div ref={listRef} style={{ display: 'flex', flexDirection: 'column', gap: 6, userSelect: drag ? 'none' : undefined, WebkitUserSelect: drag ? 'none' : undefined }}>
              {day.exercises.map((pe, idx) => {
                const prev = day.exercises[idx - 1], next = day.exercises[idx + 1];
                return (
                  <ExerciseRow
                    key={pe.id}
                    pe={pe}
                    idx={idx}
                    count={day.exercises.length}
                    program={program}
                    day={day}
                    units={units}
                    catalog={catalog}
                    exercises={exercises}
                    actions={actions}
                    expanded={expandedEx === pe.id}
                    onToggle={() => setExpandedEx(expandedEx === pe.id ? null : pe.id)}
                    onDelete={() => onDeleteExercise(pe)}
                    dragging={drag?.idx === idx}
                    dragDy={drag?.idx === idx ? drag.dy : 0}
                    onStartDrag={(e) => startDrag(e, idx)}
                    ssTop={!!pe.supersetId && prev?.supersetId === pe.supersetId}
                    ssBottom={!!pe.supersetId && next?.supersetId === pe.supersetId}
                    next={next}
                  />
                );
              })}
            </div>
            <button type="button" onClick={onAdd} style={{
              marginTop: day.exercises.length ? 10 : 0, width: '100%', height: 48, borderRadius: LL.r.md,
              background: LL.accentSoft, border: 'none', color: LL.accentText,
              fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer',
            }}><PIcon.plus /> Add exercises</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ——— Exercise row ——————————————————————————————————————————————————————————

function ExerciseRow({ pe, idx, count, program, day, units, catalog, exercises, actions, expanded, onToggle, onDelete, dragging, dragDy, onStartDrag, ssTop, ssBottom, next }) {
  const { simple } = useSimple();
  const [videoOpen, setVideoOpen] = React.useState(false);
  const rx = prescriptionFor(program, pe);
  const name = rx.name || pe.name;
  const scheme = schemeOf(pe);
  const { state: appState, toast } = useApp();
  const plan = React.useMemo(() => (scheme === 'double' ? null : prescribe({ program, pe, weekIndex: programWeekInfo({ program, sessions: appState.sessions }).index, units, history: appState.sessions })), [scheme, program, pe, units, appState.sessions]);
  const ex = exercises.find(e => e.id === pe.exerciseId) || null;
  const rawUrl = rx.yt || pe.yt || ex?.yt || '';
  const hasVideo = React.useMemo(() => !!parseYouTube(rawUrl), [rawUrl]);
  const isSec = pe.unit === 'sec';
  const ul = unitLabel(units);
  const up = (p) => actions.updateExerciseInDay(program.id, day.id, pe.id, p);
  const item = ex ? thumbItem(ex, catalog) : { primary: [pe.group], secondary: [] };
  const w = pe.targetWeight || 0;
  const inSS = !!pe.supersetId;
  // Bodyweight movements show "bodyweight" (or "+N" added load), never "find weight".
  const bw = isSec || /^Bodyweight/.test(ex?.equipment || '') || /push-?ups?\b|pull-?ups?\b|chin-?up|\bdips?\b|plank|crunch|bridge|inverted row|leg raise|sit-?up/i.test(name);

  const linkNext = () => {
    if (!next) return;
    const gid = pe.supersetId || next.supersetId || ('ss_' + Math.random().toString(36).slice(2, 8));
    up({ supersetId: gid });
    actions.updateExerciseInDay(program.id, day.id, next.id, { supersetId: gid });
  };
  const unlink = () => {
    const group = day.exercises.filter(e => e.supersetId && e.supersetId === pe.supersetId);
    up({ supersetId: null });
    if (group.length === 2) group.filter(e => e.id !== pe.id).forEach(e => actions.updateExerciseInDay(program.id, day.id, e.id, { supersetId: null }));
  };

  return (
    <div data-pe-row style={{ position: 'relative', paddingLeft: inSS ? 12 : 0 }}>
      {inSS && (
        <div aria-hidden style={{
          position: 'absolute', left: 3, width: 3, borderRadius: LL.r.xs, background: LL.accent,
          top: ssTop ? -6 : 14, bottom: ssBottom ? -6 : 14,
        }} />
      )}
      <div style={{
        background: dragging ? LL.surface3 : LL.surface2,
        border: `1px solid ${dragging ? `color-mix(in oklab, ${LL.accent} 60%, transparent)` : (expanded ? LL.line2 : LL.line)}`,
        borderRadius: LL.r.md,
        transform: dragging ? `translateY(${dragDy}px) scale(1.02)` : 'none',
        boxShadow: dragging ? LL.shadow.lift : 'none',
        position: 'relative', zIndex: dragging ? 5 : 1,
        transition: dragging ? `box-shadow ${LL.motion.fast}ms ${LL.ease}` : `transform ${LL.motion.base}ms ${LL.ease}, box-shadow ${LL.motion.base}ms ${LL.ease}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 6px 8px 4px', minHeight: 60, userSelect: 'none', WebkitUserSelect: 'none' }}>
          <div
            onPointerDown={onStartDrag}
            role="button"
            aria-label={`Drag to reorder ${name}`}
            title="Drag to reorder"
            style={{ width: 28, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', color: LL.fgFaint, cursor: 'grab', touchAction: 'none', flexShrink: 0 }}
          ><LLIcon.grip style={{ width: 18, height: 18 }} /></div>
          <ExerciseThumb item={item} size={40} radius={LL.r.sm} />
          <button type="button" onClick={onToggle} aria-expanded={expanded} style={{ flex: 1, minWidth: 0, background: 'none', border: 'none', padding: 0, textAlign: 'left', cursor: 'pointer' }}>
            {inSS && !ssTop && <div style={{ ...kicker, fontSize: LL.fs.cap, color: LL.accentText, letterSpacing: 1, marginBottom: 1 }}>Superset</div>}
            <div style={{ fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, color: LL.fg, lineHeight: 1.25, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', overflowWrap: 'anywhere' }}>{name}</div>
            {plan ? (
            <div style={{ ...meta, fontSize: LL.fs.sm, marginTop: 2, display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', overflow: 'hidden' }}>
              {(plan.needsTM || plan.flagged) && <span aria-label={plan.needsTM ? 'Needs a training max' : 'Joint pain flagged'} style={{ width: 6, height: 6, borderRadius: LL.r.xs, background: LL.warn, flexShrink: 0 }} />}
              <SchemeTag scheme={scheme} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{rowsSummary(plan.rows, units, { weights: !plan.needsTM })}</span>
            </div>
            ) : (
            <div style={{ ...meta, fontSize: LL.fs.sm, marginTop: 2, display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}>
              <span>{rx.sets} × {fmtReps(rx.repMin, rx.repMax)}{isSec ? ' s' : ''}</span>
              <span aria-hidden style={{ color: LL.fgFaint }}>·</span>
              {w > 0
                ? <span>{bw ? '+' : ''}{fmtWeight(w, units)} {ul}</span>
                : <span style={{ color: bw ? LL.fgMute : LL.accentText }}>{bw ? 'bodyweight' : 'find weight'}</span>}
              <span aria-hidden style={{ color: LL.fgFaint }}>·</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><PIcon.clock style={{ width: 13, height: 13 }} />{fmtRest(rx.rest ?? pe.rest ?? 120)}</span>
              {rx.rpeLast != null && <span>· {simple ? plainEffortShort(rx.rpeLast) : `RPE ${rx.rpeLast}`}</span>}
            </div>
            )}
          </button>
          {hasVideo && (
            <IconBtn title={`Watch ${name} video`} tone="ghost" size={34} onClick={() => setVideoOpen(true)}><LLIcon.video style={{ width: 17, height: 17 }} /></IconBtn>
          )}
          <IconBtn title={expanded ? 'Done' : `Edit ${name}`} tone="ghost" size={34} onClick={onToggle}>
            <LLIcon.chevDown style={{ width: 18, height: 18, transform: expanded ? 'rotate(180deg)' : 'none', transition: `transform ${LL.motion.base}ms ${LL.ease}` }} />
          </IconBtn>
        </div>

        {expanded && (
          <div className="llp-anim" style={{ borderTop: `1px solid ${LL.line}`, padding: '12px 12px 12px', animation: reducedMotion() ? undefined : `llpFadeUp ${LL.motion.base}ms ${LL.ease} both` }}>
            <SchemeEditor program={program} day={day} pe={pe} units={units} actions={actions} sessions={appState.sessions} toast={toast} bw={bw} isSec={isSec} />
            {plan?.note && <div style={{ ...meta, fontSize: LL.fs.sm, marginTop: 8, color: LL.fgDim, lineHeight: 1.45, padding: '0 2px' }}>{plan.note}</div>}
            {(rx.notes || pe.note) && <div style={{ ...meta, fontSize: LL.fs.sm, marginTop: 10, color: LL.fgDim }}>{rx.notes || pe.note}</div>}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
              <Btn tone="secondary" size="sm" icon={<PIcon.up style={{ width: 16, height: 16 }} />} disabled={idx === 0} onClick={() => actions.reorderExercises(program.id, day.id, idx, idx - 1)}>Up</Btn>
              <Btn tone="secondary" size="sm" icon={<PIcon.down style={{ width: 16, height: 16 }} />} disabled={idx >= count - 1} onClick={() => actions.reorderExercises(program.id, day.id, idx, idx + 1)}>Down</Btn>
              {inSS
                ? <Btn tone="secondary" size="sm" icon={<PIcon.link style={{ width: 16, height: 16 }} />} onClick={unlink}>Unlink superset</Btn>
                : next && <Btn tone="secondary" size="sm" icon={<PIcon.link style={{ width: 16, height: 16 }} />} onClick={linkNext}>Superset with next</Btn>}
              <div style={{ flex: 1 }} />
              <Btn tone="danger" size="sm" icon={<PIcon.trash style={{ width: 16, height: 16 }} />} onClick={onDelete}>Remove</Btn>
            </div>
          </div>
        )}
      </div>
      <VideoPlayerSheet open={videoOpen} onClose={() => setVideoOpen(false)} videoUrl={rawUrl} exerciseName={name} />
    </div>
  );
}

export { ProgramsScreen, ProgramEditor };
